import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API, authFetch } from "../lib/api";
import { toast } from "sonner";
import { format, parseISO, differenceInDays } from "date-fns";
import { Plane, Car, Bed, Sparkles, AlertTriangle, Edit3, Trash2, ArrowLeft, CheckCircle2, XCircle, Hourglass, FileDown, Share2, Send, Mail, CalendarPlus, Skull } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import HousekeepingEditModal from "../components/HousekeepingEditModal";

const BED_CONFIG_LABEL = {
  double_2: "Double Bed - 2 People",
  double_1: "Double Bed - 1 Person",
  twin_2: "Twin Beds - 2 People",
  single_1: "Single Bed - 1 Person",
  bottom: "Bunk - Bottom",
  top: "Bunk - Top",
  both: "Bunk - Both",
};

const HK_PILL = {
  to_send: { label: "To send", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" },
  sent: { label: "Sent", cls: "bg-[#FDFBF7] text-[#6B705C] border-[#E8E1D5]" },
  confirmed: { label: "Confirmed", cls: "bg-[#E6F0E8] text-[#3F6B4F] border-[#C9DAC9]" },
  cancelled: { label: "Cancelled", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6] opacity-60" },
  covered: { label: "Covered by another stay", cls: "bg-[#FBF7EF] text-[#9A9A9A] border-[#E8E1D5]" },
};
const CAR_PILL = {
  to_order: { label: "To order", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" },
  waiting_confirmation: { label: "Waiting", cls: "bg-[#FDFBF7] text-[#6B705C] border-[#E8E1D5]" },
  booked: { label: "Booked", cls: "bg-[#E6F0E8] text-[#3F6B4F] border-[#C9DAC9]" },
  cancelled: { label: "Cancelled", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6] opacity-60" },
  covered: { label: "Covered by another booking", cls: "bg-[#FBF7EF] text-[#9A9A9A] border-[#E8E1D5]" },
};

export default function BookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [people, setPeople] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelInput, setCancelInput] = useState("");
  const [carEmail, setCarEmail] = useState(null);
  const [carEmailOpen, setCarEmailOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [hkEdit, setHkEdit] = useState(null);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);

  const load = () => api.get(`/bookings/${id}`).then((r) => setB(r.data));
  useEffect(() => {
    load().catch(console.error);
    api.get("/people").then((r) => setPeople(r.data)).catch(console.error);
    api.get("/settings").then((r) => setSettings(r.data)).catch(console.error);
  }, [id]);

  // Admin-only: load all users so they can pick a new owner for the stay.
  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/users")
        .then((r) => setAdminUsers((r.data?.users) || r.data || []))
        .catch(console.error);
    }
  }, [user?.is_admin]);

  const reassign = async () => {
    if (!reassignTo) return;
    try {
      const { data } = await api.post(`/bookings/${id}/reassign`, { new_user_id: reassignTo });
      setB(data);
      setReassignOpen(false);
      toast.success("Stay re-assigned");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-assign");
    }
  };

  if (!b) return <div className="text-sm text-[#9A9A9A]">Loading…</div>;

  const guestNames = [
    ...b.guest_person_ids.map((pid) => people.find((p) => p.person_id === pid)?.name).filter(Boolean),
    ...b.extra_guest_names,
  ];

  const approve = async () => {
    try {
      await api.post(`/bookings/${id}/approve`);
      toast.success("Stay approved — requester notified");
      load();
    } catch (e) { toast.error("Could not approve"); }
  };

  const reject = () => {
    setConfirm({
      title: "Reject this stay?",
      message: "It will be cancelled and the requester notified by email.",
      confirmLabel: "Reject stay",
      danger: true,
      onConfirm: async () => {
        try {
          await api.post(`/bookings/${id}/reject`);
          toast.success("Stay rejected");
          load();
        } catch (e) { toast.error("Could not reject"); }
      },
    });
  };

  const cancel = async () => {
    try {
      await api.delete(`/bookings/${id}`, { data: { confirm_title: cancelInput } });
      toast.success("Stay cancelled");
      nav("/calendar");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  const hardDelete = async () => {
    try {
      await api.delete(`/bookings/${id}/hard`, { data: { confirm: deleteInput } });
      toast.success("Stay permanently deleted");
      nav("/calendar");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const downloadIcal = async () => {
    try {
      const res = await authFetch(`${API}/bookings/${id}/ical`);
      if (!res.ok) throw new Error("ical fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(b.title || "stay").replace(/[^a-z0-9]+/gi, "_")}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded");
    } catch (e) {
      toast.error("Could not download calendar file");
    }
  };

  const downloadCarIcal = async (leg) => {
    try {
      const res = await authFetch(`${API}/bookings/${id}/ical/car-${leg}`);
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "ical fetch failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `car-${leg}-${(b.title || "stay").replace(/[^a-z0-9]+/gi, "_")}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Car ${leg === "pickup" ? "pick-up" : "drop-off"} added`);
    } catch (e) {
      toast.error(e.message || "Could not download calendar file");
    }
  };

  const downloadCleaningPdf = async () => {
    try {
      const res = await authFetch(`${API}/operations/housekeeping/${id}/pdf?download=1`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Could not download PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const ref = b.requests?.find((r) => r.type === "housekeeping")?.ref_number;
      const a = document.createElement("a");
      a.href = url;
      a.download = `Casa Blunt ${ref || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Could not download PDF");
    }
  };

  const shareCleaningPdf = async () => {
    try {
      const res = await authFetch(`${API}/operations/housekeeping/${id}/pdf`);
      if (!res.ok) throw new Error("PDF fetch failed");
      const blob = await res.blob();
      const ref = b.requests?.find((r) => r.type === "housekeeping")?.ref_number;
      const file = new File([blob], `Casa Blunt ${ref || id}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Casa Blunt — Cleaner brief",
          text: `Cleaner brief — ${b.title || "the stay"} (${b.arrival_date} → ${b.departure_date})`,
        });
        // Auto-mark as sent after the user confirms the share
        await api.post(`/operations/housekeeping/${id}/mark-sent`).catch(() => {});
        toast.success("Sent — marked as Sent");
        load();
      } else if (settings?.cleaner_phone) {
        const text = encodeURIComponent(`Hola — cleaner brief for ${b.title || "the casa"} on ${b.arrival_date}. PDF on its way.`);
        window.open(`https://wa.me/${settings.cleaner_phone.replace(/[^0-9]/g, "")}?text=${text}`, "_blank");
        downloadCleaningPdf();
        toast.message("WhatsApp opened — attach the downloaded PDF.");
      } else {
        downloadCleaningPdf();
        toast.message("Sharing not supported here — PDF downloaded.");
      }
    } catch (e) {
      toast.error("Could not share PDF");
    }
  };

  const openCarEmail = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}/car-email`);
      setCarEmail(data);
      setCarEmailOpen(true);
    } catch (e) { toast.error("Could not build car email"); }
  };

  const sendCarEmail = async () => {
    try {
      await api.post(`/bookings/${id}/car-email/send`);
      toast.success("Sent to the car hire company");
      setCarEmailOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    }
  };

  const markCleanerSent = async () => {
    try { await api.post(`/operations/housekeeping/${id}/mark-sent`); toast.success("Marked as sent"); load(); }
    catch { toast.error("Could not update"); }
  };
  const markCleanerConfirmed = async () => {
    try { await api.post(`/operations/housekeeping/${id}/mark-confirmed`); toast.success("Confirmed"); load(); }
    catch { toast.error("Could not update"); }
  };
  const markCarSent = async (bid) => {
    try { await api.post(`/operations/car/${bid}/mark-sent`); toast.success("Marked as sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const markCarConfirmed = async (bid) => {
    try { await api.post(`/operations/car/${bid}/mark-confirmed`); toast.success("Confirmed — emails sent to guests"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const carEmailMailto = () => {
    if (!carEmail) return "";
    const params = new URLSearchParams();
    if (carEmail.to) params.set("to", carEmail.to);
    params.set("subject", carEmail.subject);
    params.set("body", carEmail.body);
    return `mailto:${carEmail.to}?${params.toString().replace(/^to=[^&]*&/, "")}`;
  };

  const nights = differenceInDays(parseISO(b.departure_date), parseISO(b.arrival_date));
  const sym = settings?.currency_symbol || "£";
  const pending = b.approval_status === "pending";
  const rejected = b.approval_status === "rejected";
  const titleSuffix = b.primary_guest_name ? ` (${b.primary_guest_name})` : "";
  const canCancel = user && (user.is_admin || user.user_id === b.created_by);
  const canEdit = canCancel; // same rule — admin or stay creator
  const expectedTitle = (b.title || "Family stay").trim();

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="booking-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#7A7A7A] hover:text-[#2B2B2B] inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Header */}
      <div className="villa-card p-6 sm:p-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">
              {b.cost_category === "family" ? "Familia" : "Amigos"} · stay
            </p>
            <h1 className="font-serif-villa text-4xl mt-1">{b.title || "Family stay"}{titleSuffix}</h1>
            <p className="text-[#7A7A7A] mt-2">
              {format(parseISO(b.arrival_date), "EEE d MMM yyyy")} → {format(parseISO(b.departure_date), "EEE d MMM yyyy")}
              <span className="mx-2">·</span>{nights} {nights === 1 ? "night" : "nights"}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {b.status === "cancelled" && <span className="villa-chip villa-chip-warn">Cancelled</span>}
              {pending && (
                <span className="villa-chip villa-chip-warn inline-flex items-center gap-1" data-testid="approval-pending-badge">
                  <Hourglass className="w-3 h-3" /> Awaiting approval
                </span>
              )}
              {rejected && (
                <span className="villa-chip villa-chip-warn inline-flex items-center gap-1">
                  <XCircle className="w-3 h-3" /> Rejected
                </span>
              )}
              {b.approval_status === "approved" && (
                <span className="villa-chip villa-chip-ok inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Approved
                </span>
              )}
            </div>
            {b.created_by_email && (
              <p className="text-xs text-[#9A9A9A] mt-3 inline-flex items-center gap-2" data-testid="belongs-to-line">
                Belongs to <span className="text-[#2B2B2B]">{b.created_by_email}</span>
                {user?.is_admin && (
                  <button
                    onClick={() => { setReassignTo(b.created_by); setReassignOpen(true); }}
                    data-testid="reassign-btn"
                    className="text-[#C25E3E] hover:underline"
                  >Re-assign</button>
                )}
              </p>
            )}
            {b.conflicts?.length > 0 && (
              <div className="mt-4 flex items-center gap-2 text-[#A84C30] text-sm">
                <AlertTriangle className="w-4 h-4" />
                Overlaps with {b.conflicts.length} other stay{b.conflicts.length > 1 ? "s" : ""}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {user?.is_admin && pending && (
              <>
                <button
                  onClick={approve}
                  data-testid="approve-booking-btn"
                  className="px-4 py-2 rounded-full text-sm bg-[#6B705C] text-[#FDFBF7] hover:bg-[#5A6050] inline-flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={reject}
                  data-testid="reject-booking-btn"
                  className="px-4 py-2 rounded-full text-sm bg-[#FBE9E7] text-[#A84C30] border border-[#F2C9B6] hover:bg-[#F5D9D1] inline-flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" /> Reject
                </button>
              </>
            )}
            {canEdit && (
              <Link
                to={`/bookings/${id}/edit`}
                data-testid="edit-booking-btn"
                className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2"
              >
                <Edit3 className="w-4 h-4" /> Edit
              </Link>
            )}
            <button
              onClick={downloadIcal}
              data-testid="ical-download-btn"
              className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2"
            >
              <CalendarPlus className="w-4 h-4" /> Add to calendar
            </button>
            {b.status !== "cancelled" && canCancel && (
              <button
                onClick={() => { setCancelInput(""); setCancelOpen(true); }}
                data-testid="cancel-booking-btn"
                className="px-4 py-2 rounded-full text-sm bg-[#FBE9E7] text-[#A84C30] border border-[#F2C9B6] hover:bg-[#F5D9D1] inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Cancel stay
              </button>
            )}
            {user?.is_admin && (
              <button
                onClick={() => { setDeleteInput(""); setHardDeleteOpen(true); }}
                data-testid="hard-delete-booking-btn"
                title="Permanently delete this stay (admin only)"
                className="px-3 py-2 rounded-full text-sm bg-[#2B2B2B] text-[#FBE9E7] hover:bg-[#1A1A1A] inline-flex items-center gap-2 border border-[#2B2B2B]"
              >
                <Skull className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <Info label="Guests">
            {guestNames.length === 0 ? <span className="italic text-[#9A9A9A]">No guests listed</span> : guestNames.join(", ")}
          </Info>
          <Info label="Rooms"><Bed className="w-4 h-4 inline mr-1 text-[#6B705C]" />{b.rooms_needed}</Info>
          <Info label="Car"><Car className="w-4 h-4 inline mr-1 text-[#6B705C]" />{b.car_required ? "Required" : "Not needed"}</Info>
          <Info label="Arrival">{b.arrival_time || "—"} {b.arrival_flight && <span className="text-[#7A7A7A]"><Plane className="w-3 h-3 inline mx-1" />{b.arrival_flight}</span>} <span className="text-[#9A9A9A] text-xs ml-1">@ {AIRPORT_LABEL[b.arrival_airport] || "RMU"}</span></Info>
          <Info label="Departure">{b.departure_time || "—"} {b.return_flight && <span className="text-[#7A7A7A]"><Plane className="w-3 h-3 inline mx-1" />{b.return_flight}</span>} <span className="text-[#9A9A9A] text-xs ml-1">@ {AIRPORT_LABEL[b.departure_airport] || "RMU"}</span></Info>
          <Info label="Primary contact">{b.primary_contact_email || "—"}</Info>
        </div>

        {b.notes && (
          <div className="mt-6 p-4 rounded-xl bg-[#FBF7EF] border border-[#E8E1D5]">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-1">Notes</div>
            <p className="text-sm text-[#2B2B2B] whitespace-pre-wrap">{b.notes}</p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-4">
          {(settings?.cost_visibility || []).includes(b.cost_category) && (
            <>
              <div className="px-5 py-3 rounded-2xl bg-[#FDFBF7] border border-[#E8E1D5]" data-testid="cost-block">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A]">Estimated contribution</div>
                <div className="font-serif-villa text-2xl">{sym}{b.cost_amount.toFixed(0)}</div>
              </div>
              <p className="text-xs text-[#7A7A7A] max-w-sm">For cleaning, maintenance and car upkeep — informational only.</p>
            </>
          )}
        </div>
      </div>

      {/* Cleaner Brief + Car Booking + Room Layout (visible while approved & active, OR when cancelled but a brief was already sent) */}
      {b.approval_status === "approved" && (() => {
        const hk = b.requests?.find((r) => r.type === "housekeeping");
        const carReq = b.requests?.find((r) => r.type === "car");
        const cancelled = b.status === "cancelled";
        const hkBriefIssued = hk && ["sent", "confirmed"].includes(hk.status);
        const carIssued = carReq && ["waiting_confirmation", "booked"].includes(carReq.status);
        if (cancelled && !hkBriefIssued && !carIssued) return null;
        const hkPill = HK_PILL[hk?.status] || HK_PILL.to_send;
        const carPill = CAR_PILL[carReq?.status] || CAR_PILL.to_order;
        const showCleaner = !cancelled || hkBriefIssued;
        const showCar = b.car_required && (!cancelled || carIssued);
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Cleaner Brief */}
            {showCleaner && (
            <div className="villa-card p-6 sm:p-8 relative" data-testid="cleaner-brief-panel">
              {canEdit && hk?.status !== "covered" && (
                <button
                  onClick={() => hk && setHkEdit({ ...hk, booking: { title: b.title, arrival_date: b.arrival_date, departure_date: b.departure_date }})}
                  data-testid="cleaner-edit-button"
                  title="Edit"
                  disabled={!hk}
                  className="absolute top-4 right-4 text-[#7A7A7A] hover:text-[#2B2B2B] disabled:opacity-40"
                ><Edit3 className="w-4 h-4" /></button>
              )}
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Limpieza</p>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <h3 className="font-serif-villa text-xl">Cleaner brief</h3>
                {hk?.ref_number && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FBF7EF] border border-[#E8E1D5] text-[#6B705C]">{hk.ref_number}</span>}
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${hkPill.cls}`} data-testid="cleaner-status-pill">{hkPill.label}</span>
                {cancelled && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]">Stay cancelled</span>}
              </div>

              {/* Covered (this stay overlaps another and the cleaner brief is on the other one) */}
              {hk?.status === "covered" && hk?.covered_by_booking_id && (
                <div className="mt-4 p-4 rounded-xl bg-[#FBF7EF] border border-[#E8E1D5]" data-testid="cleaner-covered-block">
                  <p className="text-sm text-[#2B2B2B]">
                    <strong>Overlapping stay.</strong> The cleaner brief is shared with the earlier stay — open that one to download the PDF or send it to the cleaner.
                  </p>
                  <Link
                    to={`/bookings/${hk.covered_by_booking_id}`}
                    data-testid="cleaner-covered-link"
                    className="text-sm text-[#C25E3E] hover:underline mt-2 inline-block"
                  >Open the shared cleaner brief →</Link>
                </div>
              )}

              {hk?.status !== "covered" && (
                <>
                  <p className="text-sm text-[#7A7A7A] mt-1">
                    {cancelled
                      ? "The stay has been cancelled — the PDF below shows a CANCEL banner so the cleaner can be told."
                      : "A one-page PDF for the cleaner — covers cleaning, bed prep, laundry and any notes for this stay."}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={downloadCleaningPdf} data-testid="download-cleaning-pdf" className="villa-btn-primary px-5 py-2 rounded-full text-sm inline-flex items-center gap-2">
                  <FileDown className="w-4 h-4" /> Download PDF
                </button>
                {user?.is_admin && (
                  <button onClick={shareCleaningPdf} data-testid="share-cleaning-pdf" className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2">
                    <Share2 className="w-4 h-4" /> Send to cleaner
                  </button>
                )}
                {user?.is_admin && !cancelled && hk?.status === "to_send" && (
                  <button onClick={markCleanerSent} data-testid="mark-cleaner-sent" className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2">
                    <Send className="w-4 h-4" /> Mark sent
                  </button>
                )}
                {user?.is_admin && !cancelled && hk?.status === "sent" && (
                  <button onClick={markCleanerConfirmed} data-testid="mark-cleaner-confirmed" className="px-4 py-2 rounded-full text-sm bg-[#6B705C] text-[#FDFBF7] hover:bg-[#5A6050] inline-flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Mark confirmed
                  </button>
                )}
              </div>
              {settings?.cleaner_phone && (
                <p className="text-xs text-[#9A9A9A] mt-3">Cleaner: {settings.cleaner_name || settings.cleaner_phone}</p>
              )}
              {(() => {
                const departed = b.departure_date <= new Date().toISOString().slice(0, 10);
                const ciEur = Number(hk?.cost_check_in_clean || 0);
                const coEur = Number(hk?.cost_check_out_clean || 0);
                const laEur = Number(hk?.cost_laundry || 0);
                const totalEur = ciEur + coEur + laEur;
                if (!departed && totalEur === 0) return null;
                const lines = [
                  { label: "Check-in", eur: ciEur, gbp: hk?.gbp_paid_check_in, paid: !!hk?.paid_at_check_in, testid: "actual-checkin" },
                  { label: "Check-out", eur: coEur, gbp: hk?.gbp_paid_check_out, paid: !!hk?.paid_at_check_out, testid: "actual-checkout" },
                  { label: "Laundry", eur: laEur, gbp: hk?.gbp_paid_laundry, paid: !!hk?.paid_at_laundry, testid: "actual-laundry" },
                ];
                const applicable = lines.filter((l) => l.eur > 0);
                const paidCount = applicable.filter((l) => l.paid).length;
                const totalGbpPaid = applicable.filter((l) => l.paid).reduce((s, l) => s + Number(l.gbp || 0), 0);
                let pill = null;
                if (applicable.length > 0) {
                  if (paidCount === 0) pill = { label: "Unpaid", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" };
                  else if (paidCount < applicable.length) pill = { label: "Part paid", cls: "bg-[#FFF4E0] text-[#C25E3E] border-[#F2D8B6]" };
                  else pill = { label: "Paid", cls: "bg-[#E9F1E5] text-[#3D6242] border-[#C8DBC1]" };
                }
                return (
                  <div className="mt-4 pt-4 border-t border-[#E8E1D5]" data-testid="cleaning-actuals">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Actual cleaning costs</p>
                        {pill && <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${pill.cls}`} data-testid="cleaning-payment-status">{pill.label}</span>}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-[#7A7A7A]">Billed € · Paid £</div>
                        <div className="font-serif-villa text-base"><span data-testid="cleaning-actuals-eur-total">€{totalEur.toFixed(0)}</span> <span className="text-[#7A7A7A] mx-1">·</span> <span className="text-[#C25E3E]" data-testid="cleaning-actuals-gbp-total">£{totalGbpPaid.toFixed(0)}</span></div>
                      </div>
                    </div>
                    {totalEur === 0 ? (
                      <p className="text-xs text-[#9A9A9A] mt-1">Not logged yet — tap the pencil above to add what was spent.</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-3 mt-2 text-xs text-[#7A7A7A]">
                        {lines.map((l) => (
                          <div key={l.label} data-testid={l.testid}>
                            <div className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                              {l.label}
                              {l.paid && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3D6242]" title="Paid" />}
                            </div>
                            <div className="text-[#2B2B2B] font-medium mt-0.5">€{l.eur.toFixed(0)}</div>
                            <div className="text-[10px] text-[#7A7A7A]">{l.paid ? `£${Number(l.gbp || 0).toFixed(0)} paid` : (l.gbp != null ? `£${Number(l.gbp).toFixed(0)} pending` : "—")}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              </>
              )}
            </div>
            )}

            {/* Car Booking */}
            {showCar && (
              <div className="villa-card p-6 sm:p-8 relative" data-testid="car-booking-panel">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Coche de alquiler</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <h3 className="font-serif-villa text-xl">Car booking</h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${carPill.cls}`} data-testid="car-status-pill">{carPill.label}</span>
                  {cancelled && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]">Stay cancelled</span>}
                </div>
                <p className="text-sm text-[#7A7A7A] mt-1">
                  {AIRPORT_LABEL[b.arrival_airport]} → {AIRPORT_LABEL[b.departure_airport]}
                  {(b.arrival_airport === "ALC" || b.departure_airport === "ALC") && (
                    <span className="text-[#C25E3E]"> · Alicante fee applies</span>
                  )}
                  {cancelled && <span className="block text-[#A84C30] mt-1">Cancelled — preview will send a cancellation note to the hire company.</span>}
                </p>
                {/* Computed drop-off time chip — gives the family the "be at the desk by …" answer at a glance */}
                {b.car_required && b.departure_time && (() => {
                  const lead = b.car_drop_off_lead_minutes ?? (b.departure_airport === "ALC" ? 120 : 75);
                  const [hh, mm] = b.departure_time.split(":").map(Number);
                  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
                  const total = ((hh * 60 + mm) - lead + 24 * 60) % (24 * 60);
                  const dropTime = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
                  const leadStr = lead >= 60
                    ? (lead % 60 === 0 ? `${lead / 60}h` : `${Math.floor(lead / 60)}h ${String(lead % 60).padStart(2, "0")}m`)
                    : `${lead}m`;
                  return (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FBF7EF] border border-[#E8E1D5] text-xs text-[#2B2B2B]" data-testid="car-dropoff-chip">
                      <Car className="w-3.5 h-3.5 text-[#457B9D]" />
                      Drop off by <strong className="font-serif-villa">{dropTime}</strong>
                      <span className="text-[#9A9A9A]">· flight {b.departure_time} · {leadStr} earlier</span>
                    </div>
                  );
                })()}
                {b.car_required && (b.arrival_time || b.departure_time) && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="car-ical-buttons">
                    {b.arrival_time && (
                      <button
                        onClick={() => downloadCarIcal("pickup")}
                        data-testid="car-ical-pickup"
                        className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1.5"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" /> Pick-up to calendar
                      </button>
                    )}
                    {b.departure_time && (
                      <button
                        onClick={() => downloadCarIcal("dropoff")}
                        data-testid="car-ical-dropoff"
                        className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1.5"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" /> Drop-off to calendar
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {carReq?.status !== "covered" && (
                    <>
                      {user?.is_admin && (
                        <button onClick={openCarEmail} data-testid="open-car-email" className="villa-btn-primary px-5 py-2 rounded-full text-sm inline-flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Preview & send
                        </button>
                      )}
                      {user?.is_admin && !cancelled && carReq?.status === "to_order" && (
                        <button onClick={() => markCarSent(id)} data-testid="mark-car-sent" className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2">
                          <Send className="w-4 h-4" /> Mark sent
                        </button>
                      )}
                      {user?.is_admin && !cancelled && carReq?.status === "waiting_confirmation" && (
                        <button onClick={() => markCarConfirmed(id)} data-testid="mark-car-confirmed" className="px-4 py-2 rounded-full text-sm bg-[#6B705C] text-[#FDFBF7] hover:bg-[#5A6050] inline-flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" /> Mark confirmed
                        </button>
                      )}
                    </>
                  )}
                  {carReq?.status === "covered" && carReq?.covered_by_booking_id && (
                    <Link to={`/bookings/${carReq.covered_by_booking_id}`} className="text-sm text-[#C25E3E] hover:underline">
                      View lead car booking →
                    </Link>
                  )}
                </div>
                {settings?.car_hire_email ? (
                  <p className="text-xs text-[#9A9A9A] mt-3">To: {settings.car_hire_company ? `${settings.car_hire_company} · ` : ""}{settings.car_hire_email}</p>
                ) : (
                  <p className="text-xs text-[#A84C30] mt-3">Set the car hire email in Settings to enable sending.</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Room Layout */}
      {b.room_assignments?.length > 0 && (
        <div className="villa-card p-6 sm:p-8" data-testid="room-layout-panel">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Habitaciones</p>
          <h3 className="font-serif-villa text-xl mt-1">Room layout</h3>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2">
            {b.room_assignments.map((ra) => {
              const room = (settings?.rooms || []).find((r) => r.room_id === ra.room_id);
              return (
                <li key={ra.room_id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E8E1D5] bg-[#FBF7EF]" data-testid={`room-layout-${ra.room_id}`}>
                  <Bed className="w-4 h-4 text-[#6B705C]" />
                  <div className="min-w-0">
                    <div className="text-sm text-[#2B2B2B]">{room?.name || ra.room_id}</div>
                    <div className="text-xs text-[#7A7A7A]">{BED_CONFIG_LABEL[ra.config] || ra.config}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* History */}
      {b.history?.length > 0 && (
        <div className="villa-card p-6 sm:p-8">
          <h3 className="font-serif-villa text-xl mb-3">Audit trail</h3>
          <ul className="text-sm text-[#7A7A7A] space-y-1">
            {b.history.map((h) => (
              <li key={`${h.at}-${h.action}-${h.by || ""}`}>
                <span className="capitalize text-[#2B2B2B]">{h.action}</span> by {h.by} · {format(parseISO(h.at), "d MMM yyyy HH:mm")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Cancel confirmation modal */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2B2B]/40 p-4" data-testid="cancel-modal">
          <div className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-8 max-w-md w-full border border-[#E8E1D5] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
            <h3 className="font-serif-villa text-2xl">Cancel this stay?</h3>
            <p className="text-sm text-[#7A7A7A] mt-2">
              Pending cleaner / laundry / car requests will be cancelled too. To confirm, type the booking title:
            </p>
            <p className="mt-3 text-sm text-[#2B2B2B] font-mono bg-[#F5F0E6] rounded-lg px-3 py-2 border border-[#E8E1D5]">
              {expectedTitle}
            </p>
            <input
              autoFocus
              data-testid="cancel-confirm-input"
              className="mt-4 w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E]"
              placeholder="Type the title here"
              value={cancelInput}
              onChange={(e) => setCancelInput(e.target.value)}
            />
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button onClick={() => setCancelOpen(false)} className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Keep stay</button>
              <button
                onClick={cancel}
                disabled={cancelInput.trim() !== expectedTitle}
                data-testid="cancel-confirm-btn"
                className="px-5 py-2 rounded-full text-sm bg-[#A84C30] text-[#FDFBF7] hover:bg-[#8E3F28] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Cancel stay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign stay modal (admin only) */}
      {reassignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2B2B]/60 p-4" data-testid="reassign-modal">
          <div className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-8 max-w-md w-full border border-[#E8E1D5] shadow-[0_30px_60px_rgba(43,43,43,0.18)]">
            <h3 className="font-serif-villa text-2xl text-[#2B2B2B]">Re-assign this stay</h3>
            <p className="text-sm text-[#7A7A7A] mt-1">Pick the new owner. They'll be able to edit it; the previous owner will become read-only (unless they're an admin).</p>
            <select
              data-testid="reassign-select"
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              className="mt-4 w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E]"
            >
              <option value="">— pick a person —</option>
              {adminUsers.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || u.email} {u.email ? `· ${u.email}` : ""}{u.is_admin ? " · admin" : ""}
                </option>
              ))}
            </select>
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button onClick={() => setReassignOpen(false)} className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Cancel</button>
              <button
                onClick={reassign}
                disabled={!reassignTo || reassignTo === b.created_by}
                data-testid="reassign-confirm-btn"
                className="px-5 py-2 rounded-full text-sm bg-[#C25E3E] text-[#FDFBF7] hover:bg-[#A84C30] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >Re-assign</button>
            </div>
          </div>
        </div>
      )}

      {/* Hard delete confirmation modal (admin only) */}
      {hardDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2B2B]/60 p-4" data-testid="hard-delete-modal">
          <div className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-8 max-w-md w-full border-2 border-[#A84C30] shadow-[0_30px_60px_rgba(168,76,48,0.4)]">
            <h3 className="font-serif-villa text-2xl text-[#A84C30] inline-flex items-center gap-2"><Skull className="w-5 h-5" /> Permanently delete this stay?</h3>
            <p className="text-sm text-[#7A7A7A] mt-2">
              This <strong>cannot be undone</strong>. The booking AND all its operations (cleaner brief, car booking) will be removed. The audit trail / email log is kept for the record.
            </p>
            <p className="text-sm text-[#7A7A7A] mt-3">To confirm, type the booking title exactly:</p>
            <p className="mt-2 text-sm text-[#2B2B2B] font-mono bg-[#F5F0E6] rounded-lg px-3 py-2 border border-[#E8E1D5]">
              {expectedTitle}
            </p>
            <input
              autoFocus
              data-testid="hard-delete-confirm-input"
              className="mt-4 w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#A84C30]/40 focus:border-[#A84C30]"
              placeholder="Type the title here"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
            />
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button onClick={() => setHardDeleteOpen(false)} className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Keep stay</button>
              <button
                onClick={hardDelete}
                disabled={deleteInput.trim() !== expectedTitle}
                data-testid="hard-delete-confirm-btn"
                className="px-5 py-2 rounded-full text-sm bg-[#2B2B2B] text-[#FBE9E7] hover:bg-[#1A1A1A] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 border border-[#2B2B2B]"
              >
                <Skull className="w-4 h-4" /> Delete forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Car email preview modal */}
      {carEmailOpen && carEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2B2B]/40 p-4" data-testid="car-email-modal">
          <div className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-8 max-w-2xl w-full border border-[#E8E1D5] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
            <h3 className="font-serif-villa text-2xl">Email to car hire company</h3>
            <p className="text-sm text-[#7A7A7A] mt-1">To: {carEmail.to || "(set car hire email in Settings)"} · Subject: {carEmail.subject}</p>
            <textarea
              data-testid="car-email-body"
              className="mt-4 w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-3 text-sm font-mono whitespace-pre min-h-[280px] focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E]"
              value={carEmail.body}
              onChange={(e) => setCarEmail({ ...carEmail, body: e.target.value })}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2 justify-end">
              <button onClick={() => setCarEmailOpen(false)} className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Close</button>
              <a
                href={`mailto:${encodeURIComponent(carEmail.to || "")}?subject=${encodeURIComponent(carEmail.subject)}&body=${encodeURIComponent(carEmail.body)}`}
                data-testid="car-email-mailto"
                className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2"
              >
                <Mail className="w-4 h-4" /> Open in mail app
              </a>
              <button
                onClick={sendCarEmail}
                disabled={!carEmail.to}
                data-testid="car-email-send"
                className="villa-btn-primary px-5 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> Send via email
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal open={confirm} onClose={() => setConfirm(null)} />
      {hkEdit && (
        <HousekeepingEditModal
          request={hkEdit}
          onClose={() => setHkEdit(null)}
          onSaved={() => { setHkEdit(null); load(); }}
        />
      )}
    </div>
  );
}

const AIRPORT_LABEL = { RMU: "Murcia (RMU)", ALC: "Alicante (ALC)" };

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">{label}</div>
      <div className="text-[#2B2B2B] mt-1">{children}</div>
    </div>
  );
}
